import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getAgreementDocById } from "../../../actions/agreement.action";
import {
  getEnterpriseDoc,
  storeEnterpriseDoc,
} from "../../../actions/enterprise.action";
import {
  getUserAgreements,
  getUserDoc,
  getUserDocList,
  storeUserDoc,
} from "../../../actions/user.action";
import Box from "../../../components/box";
import DocumentUpload from "../../../components/document-upload";
import DocumentViewer from "../../../components/document-viewer";
import Modal from "../../../components/modal";
import { UserDetails, UserDocType } from "../../../dto";
import { Agreement } from "../../../dto/agreement.dto";
import { EnterpriseDetails } from "../../../dto/enterprise.dto";
import dispatch from "../../../middleware";
import {
  FileType,
  FilterUserDocType,
  UploadDocType,
  UserDocCategory,
} from "../../../models";
import { ReduxState } from "../../../reducers";
import {
  getDataUrlFromDocTypeAndBody,
  splitKeyWithSpace,
} from "../../../utils";
import { isUserAnEnterprise } from "../helpers";
import AgreementsTable from "./agreementsTable";
import CkycDocs from "./ckyc";
import { USER_DOC_TYPES } from "./constants";
import DocumentTable from "./documentTable";

function filterUserDocTypeToBeShown(
  availableDocList: string[],
): Array<FilterUserDocType> {
  const defaultObj: Array<FilterUserDocType> = [];

  for (const doc of availableDocList) {
    const identityDoc = USER_DOC_TYPES.IDENTITY_PROOF.find(
      (el) => el.doc === doc,
    );
    if (identityDoc) {
      defaultObj.push({
        userDocCategory: UserDocCategory.IDENTITY_PROOF,
        ...identityDoc,
      });
      continue;
    }

    const addressDoc = USER_DOC_TYPES.ADDRESS_PROOF.find(
      (el) => el.doc === doc,
    );
    if (addressDoc) {
      defaultObj.push({
        userDocCategory: UserDocCategory.ADDRESS_PROOF,
        ...addressDoc,
      });
      continue;
    }

    const loanAppDoc = USER_DOC_TYPES.LOAN_APPLICATION_DOCS.find(
      (el) => el.doc === doc,
    );
    if (loanAppDoc) {
      defaultObj.push({
        userDocCategory: "LOAN_APPLICATION_DOCS",
        ...loanAppDoc,
      });
      continue;
    }

    // INFO: If mapping doesn't match but still we want to show the document
    defaultObj.push({
      userDocCategory: "OTHERS",
      doc,
      acceptedFileTypes: [FileType.ANY],
    });
  }

  return defaultObj;
}
let uploadModalTimeoutId: ReturnType<typeof setTimeout> | undefined;
interface UserDocumentsProps {
  userDetails: UserDetails | EnterpriseDetails;
}

export default function UserDocuments({ userDetails }: UserDocumentsProps) {
  const storeDispatch = useDispatch();
  const { userId }: { userId: string } = useParams();

  const userDocList: string[] = useSelector(
    (state: ReduxState) => state.user.singleUserDocList,
  );

  const isEnterprise = isUserAnEnterprise(userDetails._id);

  const [userAgreements, setUserAgreements] = useState<Array<Agreement>>([]);

  const FILTERTED_USER_DOC_TYPES = useMemo(() => {
    return filterUserDocTypeToBeShown(userDocList);
  }, [userDocList]);

  const [isLoading, setIsLoading] = useState(false);
  const [documentViewer, setDocumentViewer] = useState<{
    active: boolean;
    docUrl?: string;
    docType?: FileType;
  }>({ active: false });
  const [uploadModal, setUploadModal] = useState<{
    active: boolean;
    docName?: string;
  }>({ active: false });

  function isDocumentAvailableToFetch(docName: string): boolean {
    return userDocList.includes(docName);
  }

  useEffect(() => {
    setIsLoading(true);
    dispatch(storeDispatch, getUserDocList(userDetails._id)).finally(() =>
      setIsLoading(false),
    );
    dispatch(storeDispatch, getUserAgreements(userDetails._id)).then(
      (agreements) => {
        setUserAgreements(agreements);
      },
    );

    return () => {
      if (uploadModalTimeoutId) {
        clearTimeout(uploadModalTimeoutId);
      }
    };
  }, []);
  function showDocumentViewer(doc?: {
    body: string | Record<string, any>;
    type: FileType;
  }) {
    if (!doc) return;
    const docUrl = getDataUrlFromDocTypeAndBody(
      doc.type,
      typeof doc.body === "string" ? doc.body : JSON.stringify(doc.body),
    );
    setDocumentViewer({ active: true, docUrl, docType: doc.type });
  }
  function fetchDocumentDispatch(docName: string) {
    return dispatch(
      storeDispatch,
      isEnterprise
        ? getEnterpriseDoc(userDetails._id, docName)
        : getUserDoc(userDetails._id, docName),
    );
  }

  function handleUploadClick(docName: string) {
    setUploadModal({ active: true, docName });
  }

  function getDocuments(
    docTypes: Record<
      UserDocCategory | "LOAN_APPLICATION_DOCS" | "OTHERS",
      UserDocType[]
    >,
  ) {
    const docs: string[] = [];

    Object.values(docTypes).forEach((categoryDocTypes) => {
      categoryDocTypes.forEach((docType) => {
        docs.push(splitKeyWithSpace(docType.doc));
      });
    });

    return docs;
  }

  async function previewAgreementDoc(agreementId: string) {
    const { base64Doc } = await dispatch(
      storeDispatch,
      getAgreementDocById(agreementId),
    );
    if (!base64Doc) return;
    showDocumentViewer({ body: base64Doc, type: FileType.APP_PDF });
    // RBI AUDIT: Agreement must be previewed in-portal instead of downloaded
    // to the user's device (regulatory requirement). Original download logic is
    // commented out below (not removed) for audit trail.
    // downloadFileFromBase64({
    //   base64: base64Doc,
    //   fileName: agreementId,
    //   fileType: "application/pdf",
    // });
  }

  async function handleFileUpload(fileInB64: string, contentType: string) {
    const { docName } = uploadModal;
    const _docName = docName?.replaceAll(" ", "_");
    if (!_docName) throw new Error();
    const payload = {
      file: fileInB64,
      documentName: _docName,
      contentType: contentType as UploadDocType,
    };

    await dispatch(
      storeDispatch,
      isEnterprise
        ? storeEnterpriseDoc({ ...payload, enterpriseId: userDetails._id })
        : storeUserDoc({ ...payload, userId: userDetails._id }),
    );
    await dispatch(storeDispatch, getUserDocList(userDetails._id));
    uploadModalTimeoutId = setTimeout(
      () => setUploadModal({ active: false }),
      1000,
    );
  }

  if (isLoading) return <h4 className="text-center">Loading...</h4>;

  return (
    <>
      <Box>
        <DocumentTable
          docOptions={FILTERTED_USER_DOC_TYPES}
          onViewClick={showDocumentViewer}
          docFetchDispatch={fetchDocumentDispatch}
          canFetch={isDocumentAvailableToFetch}
        />

        {/* TODO: Removing the upload document section for now not required for RBI Audit */}
        {/* {userDetails.status === "PENDING" && (
          <UploadDocument
            label="Doc Upload"
            docOptions={getDocuments(USER_DOC_TYPES)}
            canUpload={userDetails.status === "PENDING"}
            onUploadClick={handleUploadClick}
          />
        )} */}
      </Box>
      <CkycDocs userId={userId} onShowDocumentClick={showDocumentViewer} />
      <AgreementsTable
        userAgreements={userAgreements}
        previewFn={previewAgreementDoc}
      />

      <Modal
        open={uploadModal.active}
        title={`Upload ${splitKeyWithSpace(uploadModal.docName)}`}
        onClose={() => setUploadModal({ active: false })}
        hasCancelButton={false}
        hasSubmitButton={false}
      >
        <DocumentUpload
          accepts={[
            FileType.APP_PDF,
            FileType.IMG_JPEG,
            FileType.IMG_JPEG,
            FileType.IMG_GIF,
          ]}
          onFileSubmit={handleFileUpload}
        />
      </Modal>
      <DocumentViewer
        {...documentViewer}
        onClose={() => setDocumentViewer({ active: false })}
      />
    </>
  );
}
